/* eslint-env browser */
import { useState, useEffect, useRef } from 'react';

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  priority = false, 
  fill = false,
  width,
  height,
  ...props 
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(priority);
  const imgRef = useRef();

  // Intersection Observer para lazy loading
  useEffect(() => {
    if (priority) {
      setInView(true);
      return;
    }

    if (typeof window === 'undefined' || !window.IntersectionObserver) {
      setInView(true);
      return;
    }

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  // Detectar soporte WebP
  const supportsWebP = () => {
    if (typeof document === 'undefined') return false;
    
    try {
      const canvas = document.createElement('canvas');
      return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } catch (e) {
      return false;
    }
  };

  // Generar src con WebP si es compatible
  const getOptimizedSrc = (originalSrc) => {
    if (!originalSrc || !supportsWebP()) return originalSrc;
    // Solo convierte URLs locales/relativas con extensión de imagen rasterizada.
    // Las URLs externas (http/https) o sin extensión conocida se devuelven tal cual.
    if (/^https?:\/\//i.test(originalSrc)) return originalSrc;
    return originalSrc.replace(/\.(png|jpg|jpeg)$/i, '.webp');
  };

  const handleLoad = () => {
    setLoaded(true);
  };

  const handleError = () => {
    setError(true);
    setLoaded(true);
  };

  // Estilos para fill
  const fillStyles = fill ? {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  } : {};

  // Placeholder mientras carga
  const placeholder = (
    <div 
      className={`bg-gray-200 animate-pulse ${fill ? 'absolute inset-0' : ''} ${className}`}
      style={{
        width: fill ? '100%' : width,
        height: fill ? '100%' : height,
        ...(fill ? { position: 'absolute', top: 0, left: 0 } : {})
      }}
    />
  );

  return (
    <div ref={imgRef} className={fill ? 'relative w-full h-full' : ''}>
      {!loaded && !error && placeholder}
      
      {inView && (
        <img
          src={getOptimizedSrc(src)}
          alt={alt}
          width={!fill ? width : undefined}
          height={!fill ? height : undefined}
          className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
          style={{
            ...fillStyles
          }}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? 'eager' : 'lazy'}
          {...props}
        />
      )}
      
      {error && (
        <div 
          className={`bg-gray-300 flex items-center justify-center text-gray-500 text-sm ${fill ? 'absolute inset-0' : ''} ${className}`}
          style={{
            width: fill ? '100%' : width,
            height: fill ? '100%' : height,
            ...(fill ? { position: 'absolute', top: 0, left: 0 } : {})
          }}
        >
          Error al cargar imagen
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
